/**
 * Forecast Summary Cards
 *
 * Displays key metrics from the forecasting data:
 * - Total predicted deliveries
 * - Total predicted volume
 * - Volume change vs historical
 * - Top products
 *
 * Supports multiple time periods via the period/offset props.
 */

import { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { getForecastByPeriod } from '../../services/forecastService'
import { TrendingUp, TrendingDown, Package, Target, BarChart3 } from 'lucide-react'

export default function ForecastSummaryCards({
  period = '2weeks',
  offset = 0,
  loading: externalLoading
}) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        const forecast = await getForecastByPeriod(period, offset)
        setData(forecast)
      } catch (error) {
        console.error('Error loading forecast summary:', error)
        setData(null)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [period, offset])

  const isLoading = externalLoading !== undefined ? externalLoading : loading

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-16 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No forecast data available
      </div>
    )
  }

  const { summary, byProduct } = data
  const {
    predictedDeliveries,
    predictedVolume,
    historicalVolume,
    volumeChangePercent,
    customersWithPredictions,
    productLinesCount
  } = summary

  const topProduct = byProduct.length > 0 ? byProduct[0] : null
  const volumeChangePositive = volumeChangePercent >= 0

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Total Predicted Deliveries */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Predicted Deliveries</p>
              <p className="text-2xl font-bold mt-1">{predictedDeliveries}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {customersWithPredictions} customers
              </p>
            </div>
            <div className="p-2 bg-primary/10 rounded-lg">
              <Target className="h-5 w-5 text-primary" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total Predicted Volume */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Predicted Volume</p>
              <p className="text-2xl font-bold mt-1">{predictedVolume.toFixed(1)}kg</p>
              <p className="text-xs text-muted-foreground mt-1">
                {predictedDeliveries > 0
                  ? `${(predictedVolume / predictedDeliveries).toFixed(1)}kg avg/delivery`
                  : 'No data'
                }
              </p>
            </div>
            <div className="p-2 bg-[#009689]/10 rounded-lg">
              <Package className="h-5 w-5 text-[#009689]" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Volume Change vs Historical */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-muted-foreground">vs Same Period Last Year</p>
              <div className="flex items-center gap-1 mt-1">
                {volumeChangePositive ? (
                  <TrendingUp className="h-5 w-5 text-[#10B981]" />
                ) : (
                  <TrendingDown className="h-5 w-5 text-[#EF4444]" />
                )}
                <span className={`text-2xl font-bold ${volumeChangePositive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {volumeChangePositive ? '+' : ''}{volumeChangePercent.toFixed(0)}%
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Historical: {historicalVolume.toFixed(1)}kg
              </p>
            </div>
            <div className={`p-2 rounded-lg ${volumeChangePositive ? 'bg-[#10B981]/10' : 'bg-[#EF4444]/10'}`}>
              <BarChart3 className={`h-5 w-5 ${volumeChangePositive ? 'text-[#10B981]' : 'text-[#EF4444]'}`} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Predicted Product */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Top Product</p>
              <p className="text-xl font-bold mt-1 truncate">
                {topProduct ? topProduct.strain : 'N/A'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {topProduct
                  ? `${topProduct.predicted.toFixed(1)}kg predicted`
                  : 'No data'
                }
              </p>
            </div>
            <div className="p-2 bg-[#F54A00]/10 rounded-lg">
              <Package className="h-5 w-5 text-[#F54A00]" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
